import styled from 'styled-components';
import { DarkButton } from 'components/style/Global';
import { GridLayout } from 'API';
import CustomEditor from './textEditor';
import { useEffect, useState } from 'react';
import { useSaveCustomReportText } from './CustomReportsHooks/useCustomReportsText';
import { useCustomReportContext } from 'context/CustomReportContext';
import { useGetCustomReportPublishedData, useSaveNewSectionItem } from './CustomReportsHooks/useCustomReportsGrid';
import { removeTypename } from 'methods/removeTypename';
import DeleteConfirmationModal from 'components/DeleteConfirmationModall';
import { createPortal } from 'react-dom';

export const CustomReportTextStyled = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  width: 100%;
  height: 100%;
  overflow: n;
  gap: 0.1vw;
  padding: 0.4vw;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.placeholderBackground} 0%,
    ${({ theme }) => theme.placeholderBackground}F8 100%
  );
  border-radius: 8px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, ${({ theme }) => theme.border}40 50%, transparent 100%);
  }

  .action-buttons-container {
    display: flex;
    justify-content: end;
    gap: 0.6vw;
    margin-top: 1vw;
  }
  &:hover {
    .edit-btns {
      display: flex;
      flex-direction: row;
      gap: 5px;
      background-color: transparent;
    }
  }

  .edit-btns {
    display: none;
    position: absolute;
    top: 10px;
    right: 10px;

    .edit-btn {
      font-weight: 300;
      box-shadow:
        0 2px 8px rgba(0, 0, 0, 0.1),
        0 1px 3px rgba(0, 0, 0, 0.08);
      border-radius: 50%;
      cursor: pointer;
      padding: 6px;
      transition: all 0.2s ease;
      background: ${({ theme }) => theme.c50};
      color: ${({ theme }) => theme.brand};
      border: 1px solid ${({ theme }) => theme.c100}40;

      &:hover {
        outline: 1px solid ${({ theme }) => theme.c200};
        box-shadow:
          0 3px 12px rgba(0, 0, 0, 0.15),
          0 2px 6px rgba(0, 0, 0, 0.1);
        transform: scale(1.05);
        background: ${({ theme }) => theme.c100};
      }
    }
  }

  p {
    font-size: 14px;
    font-family: 'fontRegular';
    margin-bottom: 5px;
    color: ${({ theme }) => theme.primaryGray};
    font-weight: 200;
    overflow-y: scroll;
    scrollbar-width: none;
  }
  textarea {
    font-size: 12px;
    font-family: 'fontRegular';
    padding: 12px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.border};
    color: ${({ theme }) => theme.primaryGray};
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.background} 0%,
      ${({ theme }) => theme.background}F8 100%
    );
    resize: none;
    min-height: 20vh;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.06);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.brand}60;
      box-shadow:
        inset 0 1px 3px rgba(0, 0, 0, 0.06),
        0 0 0 3px ${({ theme }) => theme.brand}20;
    }
  }

  .gridBox {
    overflow-y: scroll;

    scrollbar-width: none;
    /* Headings with desktop-like styling */
    h1 {
      font-size: 28px;
      font-weight: 700;
      margin: 6px 0 2px;
      color: #222;
      text-align: left;
    }

    h2 {
      font-size: 24px;
      font-weight: 600;
      margin: 6px 0 2px;
      color: #333;
    }

    h3 {
      font-size: 20px;
      font-weight: 600;
      margin: 6px 0 2px;
      color: #444;
    }

    h4 {
      font-size: 18px;
      font-weight: 600;
      margin: 6px 0 2px;
      color: #555;
    }

    h5 {
      font-size: 16px;
      font-weight: 600;
      margin: 6px 0 2px;
      color: #666;
    }

    h6 {
      font-size: 14px;
      font-weight: 600;
      margin: 6px 0 2px;
      color: #777;
    }

    /* First heading needs less top margin */
    h1:first-child,
    h2:first-child,
    h3:first-child,
    h4:first-child,
    h5:first-child,
    h6:first-child {
      margin-top: 0;
    }

    /* Bold text */
    strong {
      font-weight: 700;
    }

    /* Bullet list styling */
    ul {
      padding-left: 24px;

      li {
        margin-bottom: 8px;
        position: relative;

        &::marker {
          color: #555;
          font-size: 0.45em !important;
        }

        /* Nested lists */
        ul {
          margin: 8px 0;
        }
      }
    }

    /* Ordered list styling */
    ol {
      padding-left: 24px;

      li {
        margin-bottom: 8px;

        &::marker {
          color: #555;
          font-weight: 600;
          font-size: 0.45em !important;
        }

        /* Nested lists */
        ol {
          margin: 8px 0;
        }
      }
    }

    /* Paragraph styling */
    p {
      &:first-child {
        margin-top: 0;
      }
    }

    /* Blockquote styling */
    blockquote {
      border-left: 3px solid #ddd;
      margin: 16px 0;
      padding-left: 16px;
      color: #666;
      font-style: italic;
    }

    /* Code block styling */
    pre {
      background-color: #f5f5f5;
      border-radius: 4px;
      padding: 12px;
      margin: 16px 0;
      overflow: auto;

      code {
        font-family: monospace;
        color: #333;
      }
    }
  }
`;

type Props = {
  layoutItem: GridLayout;
  modal?: boolean;
  handleModalType: (item: GridLayout | null, type: string | null) => void;
  deleteItem?: (sectionID: string, sectionItemID: string) => void;
  initialText: string | null;
  fetchText: (sectionItemID: string) => Promise<string | null>;
  sectionID: string | null | undefined;
  onTextSaved?: (sectionItemID: string, text: string) => void;
};

const CustomReportText = (props: Props) => {
  const { layoutItem, modal, handleModalType, deleteItem, sectionID, initialText, onTextSaved, fetchText } = props;
  const {
    safeSectionLayoutUpdate,
    sectionLayoutState,
    activeCustomReport,
    publishPlaceholderObject,
    setPublishPlaceholderObject,
  } = useCustomReportContext();
  const userIsAuthorized = activeCustomReport?.authorized;
  const [saveCustomreportText] = useSaveCustomReportText();
  const [saveNewSectionItem] = useSaveNewSectionItem();

  const [content, setContent] = useState('');
  // const [localContent, setLocalContent] = useState(initialText ?? '');
  const [publishedData] = useGetCustomReportPublishedData();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  useEffect(() => {
    if (!initialText && layoutItem.SectionItemID && fetchText) {
      if (activeCustomReport?.authorized) {
        fetchText(layoutItem.SectionItemID);
      } else {
        publishedData(layoutItem.SectionItemID).then((data) => {
          if (data) {
            fetchText(layoutItem.SectionItemID as string);
          } else {
            safeSectionLayoutUpdate((prev) => {
              return prev.map((section) => {
                if (section.SectionID === sectionID) {
                  return {
                    ...section,
                    SectionElements: section.SectionElements?.filter(
                      (item) => item?.SectionItemID !== layoutItem.SectionItemID,
                    ),
                  };
                }
                return section;
              });
            });
          }
        });
      }
    }
  }, [
    layoutItem.SectionItemID,
    initialText,
    fetchText,
    activeCustomReport?.authorized,
    publishedData,
    safeSectionLayoutUpdate,
    sectionID,
  ]);
  const textToShow = content || initialText || '';
  useEffect(() => {
    if (textToShow && layoutItem.SectionItemID) {
      const placeholdersObject = {
        position: String(layoutItem.SectionItemID),
        outputlocation: textToShow,
        outputlocationTotal: 'noTotal',
        metrics: {
          chartType: textToShow,
          metric: 'image',
          axis: 1,
        },
      };
      const IdtoUse = layoutItem.SectionItemID;
      if (publishPlaceholderObject[IdtoUse || '']) {
        // Update existing object
        setPublishPlaceholderObject((prev) => ({
          ...prev,
          [IdtoUse || '']: {
            ...prev[IdtoUse || ''],
            ...placeholdersObject,
          },
        }));
      } else {
        // Add new object
        setPublishPlaceholderObject((prev) => ({
          ...prev,
          [IdtoUse || '']: placeholdersObject,
        }));
      }
    }
  }, [textToShow, layoutItem.SectionItemID, sectionID, setPublishPlaceholderObject]);

  const handleSave = async () => {
    const updateAndClose = (id: string) => {
      const updatedSectionLayoutState = sectionLayoutState.map((section) => {
        return {
          ...section,
          SectionElements: section.SectionElements?.map((el) => {
            if (el?.SectionItemID === id || el?.GridPosition?.i === layoutItem.GridPosition?.i) {
              return {
                ...el,
                Position: layoutItem.Position,
                SectionItemID: id,
                Open: false,
                __typename: 'GridLayout',
              } as GridLayout;
            }
            return el;
          }),
        };
      });
      if (onTextSaved) {
        onTextSaved(id, content);
      }
      safeSectionLayoutUpdate(() => updatedSectionLayoutState);
    };

    const cleanedLayoutItem = removeTypename(layoutItem);

    if (!layoutItem.SectionItemID) {
      delete cleanedLayoutItem.SectionItemID;
      delete cleanedLayoutItem.Open;

      await saveNewSectionItem(sectionID as string, cleanedLayoutItem).then((res) => {
        if (res.status === 200) {
          saveCustomreportText(res.id as string, content).then((result) => {
            if (result === 'success') {
              console.log('Text saved successfully');
              updateAndClose(res.id as string);
            } else {
              console.log('Error saving text', result);
            }
          });
        } else {
          alert(res.message);
        }
      });
    } else {
      saveCustomreportText(cleanedLayoutItem.SectionItemID as string, content).then((res) => {
        if (res === 'success') {
          console.log('Text updated successfully');
          updateAndClose(cleanedLayoutItem.SectionItemID as string);
        } else {
          console.log('Error updating text', res);
        }
      });
    }
  };

  return (
    <CustomReportTextStyled>
      {!modal && userIsAuthorized && (
        <div className="edit-btns">
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleModalType && handleModalType(layoutItem, 'text');
            }}
            className="edit-btn icon material-symbols-outlined"
          >
            edit
          </div>
          <div className="edit-btn icon material-symbols-outlined" onClick={() => setShowDeleteModal(true)}>
            delete
          </div>
        </div>
      )}
      {modal ? (
        <>
          <CustomEditor content={textToShow} setContent={setContent}></CustomEditor>
          <div className="action-buttons-container">
            <button onClick={() => handleModalType(null, null)} className="action-button">
              Cancel
            </button>
            <DarkButton onClick={handleSave} className="action-button">
              Save
            </DarkButton>
          </div>
        </>
      ) : (
        <div className="gridBox" dangerouslySetInnerHTML={{ __html: textToShow || '' }}></div>
      )}

      {showDeleteModal &&
        createPortal(
          <DeleteConfirmationModal
            handleDeleteConfirmationModal={(confirmed) => {
              if (confirmed) {
                deleteItem && deleteItem(sectionID as string, layoutItem.SectionItemID as string);
              }
              setShowDeleteModal(false);
            }}
            modalObject={{
              title: 'Delete Text Element',
              message: 'Are you sure you want to delete this text element? This action cannot be undone.',
              mainButton: 'Delete',
              secondaryButton: 'Cancel',
            }}
          />,
          document.body,
        )}
    </CustomReportTextStyled>
  );
};

export default CustomReportText;
